import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import type { FinAuditEntry } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

export function FinanceAuditTrailPage() {
  const [entries, setEntries] = useState<FinAuditEntry[]>([]);
  const { rows: paginatedEntries, tablePaginationProps: entriesPaginationProps } = useClientPaginatedRows(entries);

  const load = () =>
    financeApi.platformAuditTrail(100).then(setEntries).catch((e) => showApiError(e, 'Failed to load audit trail'));

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <PageHeader
        title="Finance audit trail"
        description="Who changed what, when — with IP, device, and before/after snapshots"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Audit trail' },
        ]}
        actions={
          <Button variant="secondary" size="sm" onClick={load}>
            Refresh
          </Button>
        }
      />

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3">When</th>
              <th className="p-3">Entity</th>
              <th className="p-3">Action</th>
              <th className="p-3">Actor</th>
              <th className="p-3">IP</th>
              <th className="p-3">Reason</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-500">
                  No audit entries yet. Changes to rules, documents, and compliance are tracked automatically.
                </td>
              </tr>
            ) : (
              paginatedEntries.map((e) => (
                <tr key={e.id} className="border-t">
                  <td className="p-3 whitespace-nowrap">{e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}</td>
                  <td className="p-3">{e.entityType} #{e.entityId}</td>
                  <td className="p-3">{e.actionCode}</td>
                  <td className="p-3">{e.actorUserId ?? '—'}</td>
                  <td className="p-3">{e.actorIp ?? '—'}</td>
                  <td className="p-3 max-w-xs truncate">{e.reason ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination {...entriesPaginationProps} hasRows={entries.length > 0} />
      </div>
    </div>
  );
}
